import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useAuth } from '@web/src/hooks/use-auth'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { CircleUserRoundIcon, EyeOffIcon, PlayIcon } from 'lucide-react'
import { type ComponentPropsWithoutRef, forwardRef, useEffect, useState } from 'react'
import { useSFContext } from 'src/contexts/sf-context'
import { videoDurationStr } from 'src/lib/utilsClient'
import type { MediaTypeEnum, NSFWRatingEnum, ReviewEnum } from 'types/graphql'
import SFMediaAiDetection from '../sf-media-ai-detection/sf-media-ai-detection'
import SFMediaReviewStatus from '../sf-media-review-status/sf-media-review-status'

export interface SFMediaProps {
  id?: string
  videoDuration?: number
  mediaType?: MediaTypeEnum
  reviewStatus?: ReviewEnum
  nsfwRating?: NSFWRatingEnum | string
  aiScore?: number | null
  imageUrlSmall?: string
  imageUrlMedium?: string
  imageUrlLarge?: string
  ownerId?: string
}

interface Props extends ComponentPropsWithoutRef<'div'> {
  media: SFMediaProps
  size?: 'sm' | 'md' | 'lg'
  missingImage?: string
  children?: React.ReactNode
  backgroundSize?: string
  backgroundPosition?: string
  /** Blurred, cover-sized duplicate behind the main layer (letterboxed card look). */
  blurredBackdrop?: boolean
  /** Which corner the AI detection badge sits in. */
  aiBadgeSide?: 'left' | 'right'
  disableNSFWBlur?: boolean
  disableNSFWIcon?: boolean
  showLockedPhoto?: boolean
  imageProps?: ComponentPropsWithoutRef<'img'>
}

function imageSelection(media: SFMediaProps, size: 'sm' | 'md' | 'lg') {
  if (size === 'sm') {
    return media?.imageUrlSmall
  }
  if (size === 'md') {
    return media?.imageUrlMedium
  }
  if (size === 'lg') {
    return media?.imageUrlLarge
  }
}

export function shouldBlurContent(
  userNSFWSetting: NSFWRatingEnum | undefined,
  mediaNSFWRating: NSFWRatingEnum | undefined
): boolean {
  // If no media rating, blur by default
  if (!mediaNSFWRating) {
    return true
  }

  // Define the hierarchy levels
  const nsfwHierarchy = {
    ['SFW']: 0,
    ['SUGGESTIVE']: 1,
    ['EROTICA']: 2,
    ['EXPLICIT']: 3,
    ['UNDEFINED']: 3,
  }

  let userLevel = 0
  if (userNSFWSetting) {
    userLevel = nsfwHierarchy[userNSFWSetting]
  } else {
    userLevel = nsfwHierarchy['SFW']
  }

  const mediaLevel = nsfwHierarchy[mediaNSFWRating] ?? 4

  // Blur if media level is higher than user's setting
  return mediaLevel > userLevel
}

const SFMediaImage = forwardRef<HTMLDivElement, Props>((props, forwardedRef) => {
  const {
    media,
    missingImage = '/missing-image.webp',
    size = 'md',
    children,
    backgroundSize = 'cover',
    backgroundPosition = 'center',
    blurredBackdrop = false,
    aiBadgeSide = 'right',
    disableNSFWBlur = false,
    disableNSFWIcon = false,
    showLockedPhoto = false,
    imageProps,
    className,
    ...rest
  } = props
  const [imageSrc, setImageSrc] = useState(imageSelection(media, size))
  const { currentUser } = useAuth()
  const { isNSFWEnabled } = useSFContext()
  const appConfig = getAppConfig()
  const { t } = useTranslation()

  const { className: imageClassName, onError: imageOnError, alt: imageAlt, ...restImageProps } = imageProps ?? {}

  useEffect(() => {
    setImageSrc(imageSelection(media, size))
  }, [size, media, media?.imageUrlSmall, media?.imageUrlMedium, media?.imageUrlLarge])

  /** URL shown in layers (falls back to missingImage when no media URL or after load error). */
  const displaySrc = imageSrc || missingImage

  if (media && !media?.ownerId && appConfig.DOPPLER_ENVIRONMENT !== 'production') {
    console.warn('No Media Owner')
  }

  const lockedPhoto = currentUser?.profile?.profileMedia?.id === media?.id

  // Updated blur logic to consider global blur setting
  const shouldBlur =
    !disableNSFWBlur &&
    isNSFWEnabled &&
    imageSrc &&
    imageSrc !== missingImage &&
    shouldBlurContent(currentUser?.profile?.settingNSFW as NSFWRatingEnum, media?.nsfwRating as NSFWRatingEnum)

  const backdropBgStyle = {
    backgroundImage: `url(${displaySrc})`,
    backgroundPosition,
    backgroundRepeat: 'no-repeat' as const,
    backgroundSize: 'cover' as const,
  }

  const mainBgStyle = {
    ...backdropBgStyle,
    backgroundSize,
    filter: shouldBlur ? 'blur(12px)' : 'none',
  }

  return (
    <div
      className={cn('relative h-full w-full justify-self-center overflow-hidden bg-muted', className)}
      ref={forwardedRef}
      {...rest}
    >
      {/* Blurred cover layer behind contained main image (letterboxed card look). */}
      {blurredBackdrop && displaySrc ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 scale-[1.12] blur-[28px]"
          style={backdropBgStyle}
        />
      ) : null}

      {/* Separate background layer that can be blurred */}
      <div aria-hidden className="pointer-events-none absolute inset-0" style={mainBgStyle} />

      {/* Helper image for error handling */}
      <img
        {...restImageProps}
        alt={imageAlt ?? t('Media Image')}
        aria-hidden
        className={cn('h-0 w-0', imageClassName)}
        height={0}
        onError={(e) => {
          imageOnError?.(e)
          // If video is processing just display black, which doesn't exist but the box is black so this works.
          if (media?.mediaType === 'VIDEO' && (media?.videoDuration === null || media?.videoDuration === undefined)) {
            setImageSrc('/video-processing.webp')
          } else {
            setImageSrc(missingImage)
          }
        }}
        src={imageSrc}
        width={0}
      />
      {/* This image component is a helper we use its onError to fix the background image if it fails to load. */}

      {/* All overlay content remains sharp (above backdrop + main image layers). */}
      <div className="relative h-full w-full">
        <SFMediaReviewStatus reviewStatus={media?.reviewStatus} />

        <SFMediaAiDetection
          aiScore={media?.aiScore}
          className={cn('absolute top-1 z-[1]', aiBadgeSide === 'left' ? 'left-1' : children ? 'right-9' : 'right-1')}
          popoverAlign={aiBadgeSide === 'left' ? 'start' : 'end'}
        />

        {/* Current Profile Photo Indicator */}
        {!!showLockedPhoto && lockedPhoto && (
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="absolute top-1 left-1 inline-flex text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.8)]" />
              }
            >
              <CircleUserRoundIcon className="size-6" />
            </TooltipTrigger>
            <TooltipContent>{t('Current Profile Photo.')}</TooltipContent>
          </Tooltip>
        )}

        {/* NSFW Blur Indicator */}
        {shouldBlur && !disableNSFWIcon && (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute top-1/2 block size-[20%] -translate-x-1/2 -translate-y-1/2 text-white drop-shadow-[0_0_4px_rgba(0,0,0,0.8)]',
              media?.mediaType === 'VIDEO' ? 'left-[65%]' : 'left-1/2'
            )}
          >
            <EyeOffIcon className="size-full" />
          </span>
        )}

        {media?.mediaType === 'VIDEO' && (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute top-1/2 block size-[30%] -translate-x-1/2 -translate-y-1/2 text-white drop-shadow-[0_0_4px_rgba(0,0,0,0.8)]',
              shouldBlur && !disableNSFWIcon ? 'left-[35%]' : 'left-1/2'
            )}
          >
            <PlayIcon className="size-full fill-white" />
          </span>
        )}
        {media?.mediaType === 'VIDEO' && (
          <Badge className="absolute right-1 bottom-1 rounded-md" variant="secondary">
            {media?.videoDuration ? videoDurationStr(media?.videoDuration) : t('Processing Video...')}
          </Badge>
        )}
        {children}
      </div>
    </div>
  )
})

SFMediaImage.displayName = 'SFMediaImage'

export default SFMediaImage
